import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Pagination,
  Skeleton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  VisibilityOutlined as VisibilityIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useCoordinatorRequests } from "../../../hooks/useTrainers";
import RequestDetailsModal from "./RequestDetailsModal";

// Helper to format ISO date string to Date & Time
const formatDateTime = (isoString) => {
  if (!isoString) return { date: "—", time: "" };
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return { date: String(isoString), time: "" };
    const date = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { date, time };
  } catch (e) {
    return { date: String(isoString), time: "" };
  }
};

// Helper to style status badges
const getStatusChipStyle = (status = "") => {
  const s = String(status).toLowerCase();
  switch (s) {
    case "approved":
      return {
        label: "Approved",
        bg: "#ECFDF5",
        color: "#15803D",
        border: "1px solid #D1FAE5",
      };
    case "pending":
      return {
        label: "Pending",
        bg: "#FFFBEB",
        color: "#D97706",
        border: "1px solid #FEF3C7",
      };
    case "in review":
      return {
        label: "In Review",
        bg: "#EFF6FF",
        color: "#2563EB",
        border: "1px solid #DBEAFE",
      };
    case "rejected":
      return {
        label: "Rejected",
        bg: "#FEF2F2",
        color: "#DC2626",
        border: "1px solid #FEE2E2",
      };
    default:
      return {
        label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending",
        bg: "#F1F5F9",
        color: "#64748B",
        border: "1px solid #E2E8F0",
      };
  }
};

// Helper to extract domain names array from requested_trainer.domains object
export const extractDomains = (domainsObj) => {
  if (!domainsObj) return [];
  if (Array.isArray(domainsObj)) return domainsObj;
  const fdp = Array.isArray(domainsObj.fdp) ? domainsObj.fdp.map((d) => d.domain_name || d) : [];
  const techcamp = Array.isArray(domainsObj.techcamp)
    ? domainsObj.techcamp.map((d) => d.domain_name || d)
    : [];
  return Array.from(new Set([...fdp, ...techcamp]));
};

const InstituteRequestsTable = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Debounce search input by 400ms to avoid unnecessary API calls and race conditions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  // React Query hook for fetching coordinator trainer change requests
  const {
    data: requestsResponse,
    isLoading: loading,
    isFetching,
    refetch,
  } = useCoordinatorRequests({
    search: debouncedSearch,
    page,
    limit: rowsPerPage,
  });

  const requestsList = requestsResponse?.data || [];
  const totalCount = requestsResponse?.total || 0;
  const totalPages = requestsResponse?.total_pages || 1;

  const startRecord = totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endRecord = Math.min(page * rowsPerPage, totalCount);

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setModalOpen(true);
  };

  return (
    <Box sx={{ width: "100%", fontFamily: '"Poppins", sans-serif' }}>
      {/* Title & Subtitle Header with Search Bar */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "22px",
              fontWeight: 700,
              color: "#0F172A",
              letterSpacing: "-0.01em",
            }}
          >
            Trainer Change Requests
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "13.5px",
              color: "#64748B",
              mt: 0.5,
            }}
          >
            View and manage trainer change requests from institutes.
          </Typography>
        </Box>

        {/* Search Bar on Right Hand Side */}
        <TextField
          size="small"
          placeholder="Search institutes, trainers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setDebouncedSearch("");
                    setPage(1);
                  }}
                  sx={{ p: 0.5, color: "#94A3B8", "&:hover": { color: "#64748B" } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: { xs: "100%", sm: "320px" },
            "& .MuiOutlinedInput-root": {
              fontFamily: '"Poppins", sans-serif',
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              fontSize: "13.5px",
              "& fieldset": {
                borderColor: "#E2E8F0",
              },
              "&:hover fieldset": {
                borderColor: "#CBD5E1",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2563EB",
              },
            },
            "& .MuiInputBase-input": {
              fontFamily: '"Poppins", sans-serif',
              py: 1,
              px: 0.5,
            },
          }}
        />
      </Box>

      {/* Main Table Container Card with Fixed Bottom Pagination */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.02)",
          display: "flex",
          flexDirection: "column",
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        {/* Scrollable Table Area */}
        <TableContainer
          sx={{
            maxHeight: "540px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#F8FAFC" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "4px" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 850 }}>
            {/* Table Head */}
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontFamily: '"Poppins", sans-serif',
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0",
                    py: 1.75,
                    px: 2.5,
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  },
                }}
              >
                <TableCell sx={{ width: "60px", backgroundColor: "#F8FAFC" }}>#</TableCell>
                <TableCell sx={{ backgroundColor: "#F8FAFC" }}>Institute</TableCell>
                <TableCell sx={{ backgroundColor: "#F8FAFC" }}>Request Date</TableCell>
                <TableCell sx={{ backgroundColor: "#F8FAFC" }}>Assigned Trainer</TableCell>
                <TableCell sx={{ backgroundColor: "#F8FAFC" }}>Requested Trainer</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#F8FAFC" }}>Status</TableCell>
                <TableCell align="center" sx={{ width: "100px", backgroundColor: "#F8FAFC" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 2, px: 2.5 }}><Skeleton width={25} /></TableCell>
                    <TableCell sx={{ py: 2, px: 2.5 }}><Skeleton width={180} /></TableCell>
                    <TableCell sx={{ py: 2, px: 2.5 }}><Skeleton width={110} /></TableCell>
                    <TableCell sx={{ py: 2, px: 2.5 }}><Skeleton width={130} /></TableCell>
                    <TableCell sx={{ py: 2, px: 2.5 }}><Skeleton width={130} /></TableCell>
                    <TableCell align="center" sx={{ py: 2, px: 2.5 }}><Skeleton width={80} sx={{ mx: "auto" }} /></TableCell>
                    <TableCell align="center" sx={{ py: 2, px: 2.5 }}><Skeleton width={60} sx={{ mx: "auto" }} /></TableCell>
                  </TableRow>
                ))
              ) : requestsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: "#94A3B8", fontSize: "14px", fontWeight: 500 }}>
                      No trainer change requests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requestsList.map((row, index) => {
                  const statusStyle = getStatusChipStyle(row.status);
                  const itemIndex = (page - 1) * rowsPerPage + index + 1;
                  const { date: reqDate, time: reqTime } = formatDateTime(row.created_at);

                  return (
                    <TableRow
                      key={row.request_id || row.id || index}
                      hover
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "#F8FAFC" },
                        "& td": {
                          fontFamily: '"Poppins", sans-serif',
                          borderBottom: "1px solid #F1F5F9",
                          py: 1.85,
                          px: 2.5,
                        },
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      {/* # Index */}
                      <TableCell sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", color: "#64748B", fontWeight: 600 }}>
                        {itemIndex}
                      </TableCell>

                      {/* Institute Name & Type/Membership */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: "#0F172A",
                            lineHeight: 1.4,
                          }}
                        >
                          {row.institute?.institute_name || "—"}
                        </Typography>
                        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11.5px", color: "#64748B", mt: 0.25 }}>
                          {row.institute?.membership_name || row.institute?.institute_type || "Institute"}
                        </Typography>
                      </TableCell>

                      {/* Request Date & Time */}
                      <TableCell>
                        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
                          {reqDate}
                        </Typography>
                        {reqTime && (
                          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11.5px", color: "#64748B", mt: 0.25 }}>
                            {reqTime}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Assigned Trainer */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: row.assigned_trainer?.trainer_name ? "#0F172A" : "#94A3B8",
                            fontStyle: row.assigned_trainer?.trainer_name ? "normal" : "italic",
                          }}
                        >
                          {row.assigned_trainer?.trainer_name || "Not Assigned"}
                        </Typography>
                      </TableCell>

                      {/* Requested Trainer */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: "#0F172A",
                          }}
                        >
                          {row.requested_trainer?.trainer_name || "—"}
                        </Typography>
                      </TableCell>

                      {/* Status Chip */}
                      <TableCell align="center">
                        <Chip
                          label={statusStyle.label}
                          size="small"
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            border: statusStyle.border,
                            fontSize: "12px",
                            fontWeight: 600,
                            height: "24px",
                            borderRadius: "12px",
                            px: 1,
                          }}
                        />
                      </TableCell>

                      {/* Action Button */}
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleViewDetails(row)}
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            color: "#2563EB",
                            backgroundColor: "#F8FAFC",
                            border: "1px solid #E2E8F0",
                            textTransform: "none",
                            fontSize: "12.5px",
                            fontWeight: 600,
                            py: 0.5,
                            px: 1.5,
                            borderRadius: "8px",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              backgroundColor: "#EFF6FF",
                              borderColor: "#BFDBFE",
                              color: "#1D4ED8",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Fixed Bottom Pagination Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 3,
            py: 1.75,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#FFFFFF",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", color: "#64748B", fontWeight: 500 }}>
            Showing {startRecord} - {endRecord} of {totalCount} requests
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "13px",
                  color: "#64748B",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                Rows per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "13px",
                    color: "#0F172A",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E2E8F0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2563EB",
                    },
                    "& .MuiSelect-select": {
                      py: "4px",
                      px: "10px",
                      pr: "28px !important",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        border: "1px solid #E2E8F0",
                        "& .MuiMenuItem-root": {
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: "13px",
                          py: 0.75,
                          "&.Mui-selected": {
                            backgroundColor: "#EFF6FF",
                            color: "#2563EB",
                            fontWeight: 600,
                          },
                        },
                      },
                    },
                  }}
                >
                  {[5, 10, 15, 25].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              shape="rounded"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "12.5px",
                  color: "#64748B",
                  minWidth: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#1D4ED8",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Request Details Modal */}
      <RequestDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        request={selectedRequest}
        onSuccess={() => refetch()}
      />
    </Box>
  );
};

export default InstituteRequestsTable;

